import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { useGetProfile } from "@/services";
import type { IntentPermissions, UserIntent } from "@/store/login";
import {
	INTENT_PERMISSIONS,
	currentUserAtom,
	intentDialogAtom,
	userIntentAtom,
} from "@/store/login";
import {
	Chip,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Icon,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import type React from "react";
import { type ComponentProps, useCallback, useEffect, useState } from "react";

type IconsName = ComponentProps<typeof Icon>["name"];

type NavKey = keyof IntentPermissions["navigation"];

/** What the sidebar actually shows in each mode — kept honest by reading the permissions. */
const NAV_LABEL_KEYS: Partial<Record<NavKey, string>> = {
	orders: "dashboard.menuOrders",
	senders: "dashboard.menuSenders",
	proxy: "dashboard.menuProxy",
	buyerRelations: "dashboard.menuBuyerRelations",
	sellerRelations: "dashboard.menuSellerRelations",
	payment: "dashboard.payment",
	profile: "dashboard.menuSetting",
};

const INTENTS: {
	intent: UserIntent;
	icon: IconsName;
	accent: string;
}[] = [
	{ intent: "BUY", icon: "shopping-bag-3-line", accent: "text-success-text" },
	{ intent: "SELL", icon: "price-tag-3-line", accent: "text-warning-text" },
	{ intent: "BOTH", icon: "arrow-left-right-line", accent: "text-primary-100" },
];

/** Opens the mode switcher — use from any trigger. */
export const useOpenIntentDialog = () => {
	const setOpen = useSetAtom(intentDialogAtom);
	return useCallback(() => setOpen(true), [setOpen]);
};

const IntentOption: React.FC<{
	intent: UserIntent;
	icon: IconsName;
	accent: string;
	isSelected: boolean;
	showProxy: boolean;
	onSelect: (intent: UserIntent) => void;
}> = ({ intent, icon, accent, isSelected, showProxy, onSelect }) => {
	const { t } = useTranslation();

	const nav = INTENT_PERMISSIONS[intent].navigation;
	const features = (Object.keys(NAV_LABEL_KEYS) as NavKey[])
		.filter((key) => nav[key] && (key !== "proxy" || showProxy))
		.map((key) => t(NAV_LABEL_KEYS[key] as never));

	return (
		<button
			type="button"
			role="radio"
			aria-checked={isSelected}
			onClick={() => onSelect(intent)}
			className={cn(
				"flex w-full items-start gap-3 rounded-xl border p-3 text-start transition-colors",
				isSelected
					? "border-primary-border bg-primary-fill/5"
					: "border-secondary-border hover:bg-background-100",
			)}
		>
			<span
				className={cn(
					"flex size-9 shrink-0 items-center justify-center rounded-full bg-background-100",
					accent,
				)}
			>
				<Icon name={icon} size="md" />
			</span>

			<span className="flex-1">
				<Typography variant="title-sm" as="span" className="block">
					{t(`auth.userIntent.${intent}.title` as never)}
				</Typography>

				<Typography
					variant="body-sm"
					as="span"
					className="mt-1 block text-secondary-text-100"
				>
					{t(`auth.userIntent.${intent}.description` as never)}
				</Typography>

				<span className="mt-2 flex flex-wrap items-center gap-1.5">
					<Typography
						variant="caption"
						as="span"
						className="text-secondary-icon"
					>
						{t("auth.userIntent.shows")}:
					</Typography>
					{features.map((feature) => (
						<Chip key={feature} variant="muted" size="sm">
							{feature}
						</Chip>
					))}
				</span>
			</span>

			{/* One indicator for one job: this circle is the selection. */}
			<span
				aria-hidden
				className={cn(
					"mt-1 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
					isSelected ? "border-primary-fill" : "border-secondary-border",
				)}
			>
				{isSelected ? (
					<span className="size-2.5 rounded-full bg-primary-fill" />
				) : null}
			</span>
		</button>
	);
};

/**
 * Single source of the mode switcher: it opens itself when no mode is set yet
 * (mandatory) or when any trigger sets `intentDialogAtom`.
 */
export const UserIntentModal = (): React.ReactElement => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [currentIntent, setUserIntent] = useAtom(userIntentAtom);
	const [dialogOpen, setDialogOpen] = useAtom(intentDialogAtom);
	const user = useAtomValue(currentUserAtom);
	const { data: userProfile } = useGetProfile(
		{ path: { user_id: user?.id ?? "" } },
		{ enabled: Boolean(user?.id) && user?.user_role !== "ADMIN" },
	);
	const showProxy = userProfile?.proxy_owner_type === "PERSONAL";

	// Nobody gets into the app without picking a mode first.
	const mandatory =
		currentIntent === null && !!user && user.user_role !== "ADMIN";
	const open = mandatory || dialogOpen;

	const [selectedIntent, setSelectedIntent] = useState<UserIntent | null>(
		currentIntent,
	);

	useEffect(() => {
		if (open) setSelectedIntent(currentIntent);
	}, [open, currentIntent]);

	const close = () => setDialogOpen(false);

	const isUnchanged = selectedIntent === currentIntent;

	const handleSubmit = () => {
		if (!selectedIntent || (isUnchanged && !mandatory)) return;

		setUserIntent(selectedIntent);
		setDialogOpen(false);

		if (!mandatory) {
			toast.success(
				t("auth.userIntent.switched", {
					intent: t(`auth.userIntent.${selectedIntent}.title` as never),
				}),
			);
		}

		// The page you were on may not exist in the new mode.
		navigate({ to: "/dashboard" });
	};

	const intentName = selectedIntent
		? t(`auth.userIntent.${selectedIntent}.title` as never)
		: "";

	const primaryLabel = !selectedIntent
		? t("auth.userIntent.selectFirst")
		: mandatory
			? t("auth.userIntent.startAs", { intent: intentName })
			: isUnchanged
				? t("auth.userIntent.alreadyIn", { intent: intentName })
				: t("auth.userIntent.switchTo", { intent: intentName });

	return (
		<Dialog open={open} onOpenChange={(o) => !o && !mandatory && close()}>
			<DialogContent
				persistent={mandatory}
				className="max-h-[90vh] w-full max-w-2xl"
				onPointerDownOutside={mandatory ? (e) => e.preventDefault() : undefined}
			>
				<DialogHeader className="text-start">
					<DialogTitle>
						{mandatory
							? t("auth.userIntent.welcomeTitle")
							: t("auth.userIntent.switchTitle")}
					</DialogTitle>
				</DialogHeader>

				<DialogScrollableArea className="max-h-[70vh]">
					{/* Lives in the body: the header stays a title bar. */}
					<DialogDescription className="mb-4 text-start">
						{mandatory
							? t("auth.userIntent.description")
							: t("auth.userIntent.switchDescription")}
					</DialogDescription>

					<div role="radiogroup" className="flex flex-col gap-2">
						{INTENTS.map(({ intent, icon, accent }) => (
							<IntentOption
								key={intent}
								intent={intent}
								icon={icon}
								accent={accent}
								isSelected={selectedIntent === intent}
								showProxy={showProxy}
								onSelect={setSelectedIntent}
							/>
						))}
					</div>

					<Typography
						variant="body-sm"
						className="mt-3 flex items-start gap-2 text-secondary-text-100"
					>
						<Icon
							name="information-line"
							size="sm"
							className="mt-0.5 shrink-0"
						/>
						{mandatory
							? t("auth.userIntent.note")
							: t("auth.userIntent.safeNote")}
					</Typography>
				</DialogScrollableArea>

				<DialogFooter
					buttons={{
						...(mandatory
							? {}
							: {
									secondary: {
										label: t("common.button.cancel"),
										props: { type: "button", onClick: close },
									},
								}),
						primary: {
							label: primaryLabel,
							props: {
								type: "button",
								disabled: !selectedIntent || (isUnchanged && !mandatory),
								onClick: handleSubmit,
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};
